import React from "react"
import { Users } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion"

const Community = () => {
  const faqItems = [
    {
      question: "How do I join creator communities?",
      answer:
        "Navigate to the 'Communities' tab in your dashboard and browse available groups by category. Click 'Join' on any community that interests you. Some communities may require approval from moderators."
    },
    {
      question: "Can I create my own community?",
      answer:
        "Yes! Creators with at least 50 followers can create their own communities. Go to the 'Communities' section and click 'Create New Community'. You'll need to provide a name, description, and set community guidelines."
    },
    {
      question: "How do I report inappropriate behavior in communities?",
      answer:
        "Each community post has a 'Report' option in its dropdown menu. Select the reason for reporting and submit. Our moderation team reviews all reports within 24 hours."
    },
    {
      question: "Are there live events in communities?",
      answer:
        "Yes, many communities host regular live events, workshops, and Q&A sessions. You can find upcoming events in the 'Events' tab within each community and add them to your calendar."
    }
  ]

  const communityBenefits = [
    "Network with fellow creators in your niche",
    "Share knowledge and learn from experts",
    "Collaborate on projects and cross-promote content",
    "Access exclusive resources and opportunities",
    "Get feedback on your content ideas and strategy"
  ]

  return (
    <TopicPageLayout
      title="Creator Community"
      description="Connect with other creators, share knowledge, and grow together in our vibrant community spaces."
      icon={<Users className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Community Overview
          </h2>
          <p className="text-gray-600 mb-6">
            Our creator community is designed to help you connect with
            like-minded individuals, share experiences, and grow your skills
            and audience. No creator should have to build their business
            alone!
          </p>

          <div className="bg-[#F4F0FA]/30 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-[#3D2A60]">
              Benefits of Joining Our Communities
            </h3>
            <ul className="space-y-2">
              {communityBenefits.map((benefit, index) => (
                <li key={index} className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-[#E563A4] mr-3"></div>
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Community Types
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Niche Communities
              </h3>
              <p className="text-gray-600">
                Connect with creators in your specific category or niche.
                These communities focus on specialized topics and
                industry-specific discussions.
              </p>
            </div>

            <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Skill-based Groups
              </h3>
              <p className="text-gray-600">
                Level up specific skills with communities centered around
                video editing, photography, writing, marketing, and more.
              </p>
            </div>

            <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Regional Networks
              </h3>
              <p className="text-gray-600">
                Connect with creators in your geographic area for local
                collaborations, meetups, and region-specific advice.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Upcoming Community Events
          </h2>
          <div className="space-y-4">
            <div className="border-l-4 border-[#E563A4] pl-4 py-1">
              <h3 className="text-lg font-medium text-[#3D2A60]">
                Creator Summit 2025
              </h3>
              <p className="text-gray-600 text-sm mb-1">
                June 15-17, 2025 • Virtual Event
              </p>
              <p className="text-gray-700">
                Join thousands of creators for our annual summit featuring
                keynote speakers, workshops, and networking opportunities.
              </p>
            </div>

            <div className="border-l-4 border-[#E563A4] pl-4 py-1">
              <h3 className="text-lg font-medium text-[#3D2A60]">
                Monthly Masterclass Series
              </h3>
              <p className="text-gray-600 text-sm mb-1">
                Last Thursday of every month • 2PM EST
              </p>
              <p className="text-gray-700">
                Learn from top creators in our monthly masterclass series.
                Each session focuses on a different aspect of content creation
                and monetization.
              </p>
            </div>

            <div className="border-l-4 border-[#E563A4] pl-4 py-1">
              <h3 className="text-lg font-medium text-[#3D2A60]">
                Creator Meetups
              </h3>
              <p className="text-gray-600 text-sm mb-1">
                Various dates and locations
              </p>
              <p className="text-gray-700">
                In-person networking events organized in major cities
                worldwide. Check the events calendar for a meetup near you.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Community Guidelines
          </h2>
          <div className="prose prose-p:text-gray-600 max-w-none">
            <p>
              To ensure our communities remain positive, supportive spaces for
              all creators, please adhere to these core guidelines:
            </p>

            <ol className="list-decimal pl-5 space-y-2 text-gray-600 my-4">
              <li>
                <span className="font-medium">Be respectful:</span> Treat all
                community members with kindness and respect.
              </li>
              <li>
                <span className="font-medium">No spam:</span> Avoid excessive
                self-promotion or irrelevant content.
              </li>
              <li>
                <span className="font-medium">Give value:</span> Focus on
                contributing helpful information and insights.
              </li>
              <li>
                <span className="font-medium">Honor privacy:</span> Don't
                share sensitive information from private community
                discussions.
              </li>
              <li>
                <span className="font-medium">Embrace diversity:</span>{" "}
                Respect different perspectives, backgrounds, and experiences.
              </li>
            </ol>

            <p>
              Violations of these guidelines may result in warnings, temporary
              restrictions, or permanent removal from communities.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-[#3D2A60]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-600">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default Community
